/**
 * Table block model (spec §7.4.16): `columns` and `rows` are movable lists of maps, so moves keep each
 * column's and row's identity under concurrency; a row's `cells` map a column id to a `LoroText` holding
 * inline Markdown. A cell of a column that is gone is ignored here and removed with the column.
 */
import { LoroMap, LoroMovableList, LoroText, type LoroDoc } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";

export type Align = "left" | "center" | "right" | null;

export interface TableColumn {
  id: string;
  header: string;
  align: Align;
}

export interface TableRow {
  id: string;
  cells: Record<string, string>;
}

export interface TableState {
  caption: string;
  columns: TableColumn[];
  rows: TableRow[];
}

const text = (v: unknown) => (v instanceof LoroText ? v.toString() : typeof v === "string" ? v : "");

function list(body: LoroMap, key: "columns" | "rows"): LoroMovableList {
  const v = body.get(key);
  return v instanceof LoroMovableList ? v : body.setContainer(key, new LoroMovableList());
}

function ids(l: LoroMovableList): string[] {
  const out: string[] = [];
  for (let i = 0; i < l.length; i++) {
    const m = l.get(i);
    if (m instanceof LoroMap && typeof m.get("id") === "string") out.push(m.get("id") as string);
  }
  return out;
}

/** A new column, with an empty cell in every row. Cells are created with their row or column, never
 *  on first typing: two people typing into a cell that did not exist would each create one, and one
 *  text would be lost (spike S18). */
function newColumn(columns: LoroMovableList, rows: LoroMovableList, index: number, header = "", align: Align = null): string {
  const id = uuidv7();
  const m = columns.insertContainer(index, new LoroMap());
  m.set("id", id);
  const t = m.setContainer("header", new LoroText());
  if (header) t.insert(0, header);
  m.set("align", align);
  for (let i = 0; i < rows.length; i++) {
    const row = rows.get(i);
    const cells = row instanceof LoroMap ? row.get("cells") : null;
    if (cells instanceof LoroMap && cells.get(id) === undefined) cells.setContainer(id, new LoroText());
  }
  return id;
}

/** A new row, with a cell for every column (see `newColumn`). */
function newRow(rows: LoroMovableList, columns: LoroMovableList, index: number): string {
  const id = uuidv7();
  const m = rows.insertContainer(index, new LoroMap());
  m.set("id", id);
  const c = m.setContainer("cells", new LoroMap());
  for (const col of ids(columns)) c.setContainer(col, new LoroText());
  return id;
}

/** A new table: two columns and two rows, all empty (registry `initBody`). */
export function initTableBody(body: LoroMap): void {
  body.setContainer("caption", new LoroText());
  const columns = body.setContainer("columns", new LoroMovableList());
  const rows = body.setContainer("rows", new LoroMovableList());
  newColumn(columns, rows, 0);
  newColumn(columns, rows, 1);
  newRow(rows, columns, 0);
  newRow(rows, columns, 1);
}

export function readTable(body: LoroMap): TableState {
  const columns: TableColumn[] = [];
  const cv = body.get("columns");
  if (cv instanceof LoroMovableList) {
    for (let i = 0; i < cv.length; i++) {
      const m = cv.get(i);
      if (!(m instanceof LoroMap) || typeof m.get("id") !== "string") continue;
      const align = m.get("align");
      columns.push({ id: m.get("id") as string, header: text(m.get("header")), align: align === "left" || align === "center" || align === "right" ? align : null });
    }
  }
  const known = new Set(columns.map((c) => c.id));
  const rows: TableRow[] = [];
  const rv = body.get("rows");
  if (rv instanceof LoroMovableList) {
    for (let i = 0; i < rv.length; i++) {
      const m = rv.get(i);
      if (!(m instanceof LoroMap) || typeof m.get("id") !== "string") continue;
      const cells: Record<string, string> = {};
      const c = m.get("cells");
      if (c instanceof LoroMap) for (const col of c.keys()) if (known.has(col)) cells[col] = text(c.get(col));
      rows.push({ id: m.get("id") as string, cells });
    }
  }
  return { caption: text(body.get("caption")), columns, rows };
}

export const sameTable = (a: TableState, b: TableState) => JSON.stringify(a) === JSON.stringify(b);

function indexOf(l: LoroMovableList, id: string): number {
  for (let i = 0; i < l.length; i++) {
    const m = l.get(i);
    if (m instanceof LoroMap && m.get("id") === id) return i;
  }
  return -1;
}

function rowMap(body: LoroMap, rowId: string): LoroMap | null {
  const rows = list(body, "rows");
  const i = indexOf(rows, rowId);
  const m = i >= 0 ? rows.get(i) : null;
  return m instanceof LoroMap ? m : null;
}

/** The cell's text; created here only when a row and a column were added at the same time elsewhere. */
export function cellText(body: LoroMap, rowId: string, colId: string): LoroText | null {
  const row = rowMap(body, rowId);
  if (!row) return null;
  const cv = row.get("cells");
  const cells = cv instanceof LoroMap ? cv : row.setContainer("cells", new LoroMap());
  const t = cells.get(colId);
  return t instanceof LoroText ? t : cells.setContainer(colId, new LoroText());
}

export function headerText(body: LoroMap, colId: string): LoroText | null {
  const columns = list(body, "columns");
  const i = indexOf(columns, colId);
  const m = i >= 0 ? columns.get(i) : null;
  if (!(m instanceof LoroMap)) return null;
  const t = m.get("header");
  return t instanceof LoroText ? t : m.setContainer("header", new LoroText());
}

export function addRow(doc: LoroDoc, body: LoroMap, index: number): string {
  const rows = list(body, "rows");
  const id = newRow(rows, list(body, "columns"), Math.max(0, Math.min(index, rows.length)));
  doc.commit();
  return id;
}

export function addColumn(doc: LoroDoc, body: LoroMap, index: number): string {
  const columns = list(body, "columns");
  const id = newColumn(columns, list(body, "rows"), Math.max(0, Math.min(index, columns.length)));
  doc.commit();
  return id;
}

export function deleteRow(doc: LoroDoc, body: LoroMap, rowId: string): void {
  const rows = list(body, "rows");
  const i = indexOf(rows, rowId);
  if (i >= 0) rows.delete(i, 1);
  doc.commit();
}

/** Delete a column and, in the same commit, its cells in every row (spec §7.4.16). */
export function deleteColumn(doc: LoroDoc, body: LoroMap, colId: string): void {
  const columns = list(body, "columns");
  const i = indexOf(columns, colId);
  if (i >= 0) columns.delete(i, 1);
  const rows = list(body, "rows");
  for (let r = 0; r < rows.length; r++) {
    const m = rows.get(r);
    const cells = m instanceof LoroMap ? m.get("cells") : null;
    if (cells instanceof LoroMap && cells.get(colId) !== undefined) cells.delete(colId);
  }
  doc.commit();
}

function move(doc: LoroDoc, l: LoroMovableList, id: string, delta: number): boolean {
  const i = indexOf(l, id);
  const to = i + delta;
  if (i < 0 || to < 0 || to >= l.length) return false;
  l.move(i, to);
  doc.commit();
  return true;
}

export const moveRow = (doc: LoroDoc, body: LoroMap, rowId: string, delta: number) => move(doc, list(body, "rows"), rowId, delta);
export const moveColumn = (doc: LoroDoc, body: LoroMap, colId: string, delta: number) => move(doc, list(body, "columns"), colId, delta);

export function setAlign(doc: LoroDoc, body: LoroMap, colId: string, align: Align): void {
  const columns = list(body, "columns");
  const m = columns.get(indexOf(columns, colId));
  if (m instanceof LoroMap) m.set("align", align);
  doc.commit();
}

/**
 * Write `grid` (rows of cell strings, the first row headers when `withHeader`) into the table from the
 * cell at (`rowId`, `colId`), adding rows and columns as needed (spec §8.28 paste).
 */
export function pasteGrid(doc: LoroDoc, body: LoroMap, rowId: string | null, colId: string, grid: string[][], aligns: Align[] = []): void {
  const columns = list(body, "columns");
  const rows = list(body, "rows");
  let c0 = indexOf(columns, colId);
  if (c0 < 0) c0 = 0;
  const width = Math.max(0, ...grid.map((r) => r.length));
  while (columns.length < c0 + width) newColumn(columns, rows, columns.length);
  const colIds = Array.from({ length: width }, (_, k) => (columns.get(c0 + k) as LoroMap).get("id") as string);
  aligns.forEach((a, k) => {
    if (a !== null && k < width) (columns.get(c0 + k) as LoroMap).set("align", a);
  });
  let lines = grid;
  if (rowId === null) {
    // pasted into the header row: the first line is headers
    lines.slice(0, 1).forEach((cells) => cells.forEach((v, k) => replace(headerText(body, colIds[k]!)!, v)));
    lines = lines.slice(1);
  }
  let r0 = rowId === null ? 0 : indexOf(rows, rowId);
  if (r0 < 0) r0 = 0;
  while (rows.length < r0 + lines.length) newRow(rows, columns, rows.length);
  lines.forEach((cells, i) => {
    const id = (rows.get(r0 + i) as LoroMap).get("id") as string;
    cells.forEach((v, k) => replace(cellText(body, id, colIds[k]!)!, v));
  });
  doc.commit();
}

function replace(t: LoroText, value: string): void {
  if (t.toString() !== value) t.update(value);
}
